/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const ATTRIBUTE_KEY = 'foxsell_bundle_id';
  let syncPromise = null;

  const formatBundleIds = (bundleIds) => bundleIds.length > 0 ? `|${bundleIds.join('|')}|` : '';

  // Normalize both the legacy JSON-string format and the current pipe-delimited format.
  const normalizeAttributeValue = (value) => {
    if (!value) {
      return '';
    }

    try {
      const parsed = JSON.parse(value);
      const parsedArray = Array.isArray(parsed) ? parsed : [parsed];
      return formatBundleIds(parsedArray.map(String).filter(Boolean));
    } catch (error) {
      const bundleIds = String(value)
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);
      return formatBundleIds(bundleIds);
    }
  };

  // The FoxSell bundle-items payload is the most reliable signal that a cart line is a bundle parent.
  const getBundleIdsFromCart = (cart) => {
    const bundleIds = new Set();

    (cart?.items || []).forEach((item) => {
      if (item?.properties?.['__foxsell:dynamic_add_on_bundle_items']) {
        bundleIds.add(String(item.product_id));
      }
    });

    return Array.from(bundleIds);
  };

  const syncFoxsellBundleAttribute = async (cart) => {
    const expectedValue = formatBundleIds(getBundleIdsFromCart(cart));
    const currentValue = normalizeAttributeValue(cart?.attributes?.[ATTRIBUTE_KEY]);

    if (expectedValue === currentValue) {
      return;
    }

    await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [ATTRIBUTE_KEY]: expectedValue,
        },
      }),
    });
  };

  const loadCartAndSync = async (cartFromEvent) => {
    const cart = cartFromEvent || await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response) => response.json());

    await syncFoxsellBundleAttribute(cart);
  };

  const queueFoxsellBundleAttributeSync = (cartFromEvent) => {
    if (syncPromise) {
      syncPromise = syncPromise.finally(() => loadCartAndSync(cartFromEvent));
    } else {
      syncPromise = loadCartAndSync(cartFromEvent);
    }

    syncPromise = syncPromise.catch((error) => {
      console.error('[FoxSell] Error syncing foxsell_bundle_id cart attribute:', error);
    }).finally(() => {
      syncPromise = null;
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    queueFoxsellBundleAttributeSync();
  });

  document.documentElement.addEventListener('cart:updated', (event) => {
    queueFoxsellBundleAttributeSync(event.detail?.cart);
  });
})();

/* PATROL CHANGE START */
(() => {
  const TabsNav = window.customElements.get('tabs-nav');

  if (!TabsNav) {
    return;
  }

  const originalConnectedCallback = TabsNav.prototype.connectedCallback;
  const originalAddButton = TabsNav.prototype.addButton;

  const getSelectionAttribute = (button) => button?.getAttribute('role') === 'tab' ? 'aria-selected' : 'aria-expanded';
  const isSelected = (button) => button?.getAttribute(getSelectionAttribute(button)) === 'true';
  const setSelected = (button, selected) => {
    if (!button) {
      return;
    }

    button.setAttribute('aria-expanded', selected ? 'true' : 'false');
    button.setAttribute(getSelectionAttribute(button), selected ? 'true' : 'false');
    button.setAttribute('tabindex', selected ? '0' : '-1');
  };

  const getPanel = (button) => {
    const panelId = button?.getAttribute('aria-controls');

    return panelId ? document.getElementById(panelId) : null;
  };

  const updatePanelAnnouncement = (panel, selected) => {
    if (!panel) {
      return;
    }

    if (selected) {
      panel.setAttribute('aria-live', 'polite');
      panel.setAttribute('aria-atomic', 'true');
    } else {
      panel.removeAttribute('aria-live');
      panel.removeAttribute('aria-atomic');
    }
  };

  const enhanceTabsNav = (tabsNav) => {
    if (!tabsNav) {
      return;
    }

    tabsNav.setAttribute('role', 'tablist');
    tabsNav.buttons = Array.from(tabsNav.querySelectorAll('button[aria-controls]'));

    const fallbackSelectedButton = tabsNav.buttons.find((button) => button.getAttribute('aria-expanded') === 'true') || tabsNav.buttons[0];

    tabsNav.buttons.forEach((button) => {
      const panelId = button.getAttribute('aria-controls');
      const panel = getPanel(button);
      const selected = button === fallbackSelectedButton || isSelected(button);

      button.setAttribute('role', 'tab');
      button.id = button.id || `${panelId}-tab`;
      setSelected(button, selected);

      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.setAttribute('tabindex', '-1');
        updatePanelAnnouncement(panel, selected);
      }
    });
  };

  TabsNav.prototype.connectedCallback = function connectedCallback() {
    originalConnectedCallback.call(this);
    enhanceTabsNav(this);
  };

  TabsNav.prototype.addButton = function addButton(button) {
    originalAddButton.call(this, button);
    enhanceTabsNav(this);
  };

  Object.defineProperty(TabsNav.prototype, 'selectedTabIndex', {
    configurable: true,
    get() {
      return this.buttons.findIndex((button) => isSelected(button));
    },
  });

  Object.defineProperty(TabsNav.prototype, 'selectedButton', {
    configurable: true,
    get() {
      return this.buttons.find((button) => isSelected(button));
    },
  });

  TabsNav.prototype.selectButton = function selectButton(button, animate = true) {
    if (!this.buttons.includes(button) || this.selectedButton === button) {
      return;
    }

    const currentButton = this.selectedButton;
    const from = getPanel(currentButton);
    const to = getPanel(button);

    if (from && to) {
      if (animate) {
        this._transitionContent(from, to);
      } else {
        from.hidden = true;
        to.hidden = false;
      }
    } else if (to) {
      to.hidden = false;
    }

    setSelected(currentButton, false);
    setSelected(button, true);
    updatePanelAnnouncement(from, false);
    updatePanelAnnouncement(to, true);

    this.dispatchEvent(new CustomEvent('tabs-nav:changed', {
      bubbles: true,
      detail: { button },
    }));

    this._adjustNavigationPosition();
  };

  document.querySelectorAll('tabs-nav').forEach((tabsNav) => enhanceTabsNav(tabsNav));
})();
/* PATROL CHANGE END */
